;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { fetchIdempotentCleanup, fetchWithIdempotency } from '@workspace/request-idempotency';
import {
  AUTH_SESSION_SYNC_HEADER,
  AUTH_SESSION_SYNC_HEADER_VALUE,
  coordinateAuthTokenRefresh,
  getAuthRefreshMarker,
  waitForAuthRefreshMarkerChange,
} from '@workspace/ui/lib';
import type { ApiResponse, JwtPayload, JwtUser } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import fetchAPI from '@/lib/fetch-api';
import { getRedirectUrl } from '@/lib/get-redirect-url';
import { useAuthStore } from '@/stores/authStore';

let sessionSyncPromise: Promise<boolean> | null = null;

export const login = async (email: string, password: string, analyticsDistinctId?: string) => {
  const res = await fetchWithIdempotency('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password, analyticsDistinctId }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Login failed' }));

    // Handle 2FA case
    if (res.status === 428) {
      return { requires2FA: true, message: errorData.message };
    }

    const error = new Error(errorData.error || 'Login failed') as Error & { statusCode: number };
    error.statusCode = res.status;
    throw error;
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  const userAccessibleApp = decodedToken.user.new_app_access;

  // Use the redirect function instead of hardcoded URLs or API response
  const fe_app_url = getRedirectUrl(
    decodedToken.accessible_app,
    userAccessibleApp,
    decodedToken.roles
  );

  return {
    fe_app_url,
    requires2FA: false,
    userAccessibleApp,
    accessible_app: decodedToken.accessible_app,
    userId: decodedToken.sub,
    roles: decodedToken.roles,
    teams: decodedToken.teams,
    organisationId: decodedToken.active_organisation?.id ?? decodedToken.organisations?.[0]?.id,
  };
};

export const logout = async () => {
  try {
    const res = await fetchWithIdempotency('/api/auth/logout', { method: 'POST' });
    const data = await res.json();

    if (!res.ok) {
      console.warn('Logout API call failed:', data.error || 'Logout failed');
    }
  } catch (error) {
    console.warn('Logout failed:', error);
  }
};

export function syncAuthStateFromAccessToken(accessToken: string): void {
  if (useAuthStore.getState().accessToken === accessToken) return;

  const decodedToken = jwtDecode<JwtPayload>(accessToken);
  const user: JwtUser = {
    user: {
      id: Number(decodedToken.sub),
      first_name: decodedToken.user.first_name,
      last_name: decodedToken.user.last_name,
      email: decodedToken.user.email,
    },
    permissions: decodedToken.permissions,
    roles: decodedToken.roles,
    organisations: decodedToken.organisations ?? [],
    active_organisation: decodedToken.active_organisation ?? decodedToken.organisations?.[0],
    accessible_app: decodedToken.accessible_app,
    teams: decodedToken.teams,
  };

  useAuthStore.getState().setAccessToken(accessToken);
  useAuthStore.getState().setAuthenticated(true);
  useAuthStore.getState().setUser(user);
}

export function syncAuthSessionFromCookies(): Promise<boolean> {
  if (sessionSyncPromise) return sessionSyncPromise;

  sessionSyncPromise = (async () => {
    try {
      const response = await fetchWithIdempotency('/api/auth/session-sync', {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          [AUTH_SESSION_SYNC_HEADER]: AUTH_SESSION_SYNC_HEADER_VALUE,
        },
        cache: 'no-store',
      });

      if (response.status === 401) {
        useAuthStore.getState().setAccessToken(null);
        useAuthStore.getState().setAuthenticated(false);
        useAuthStore.getState().setUser(null);
        return true;
      }

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.access_token) return false;

      syncAuthStateFromAccessToken(data.access_token);
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    sessionSyncPromise = null;
  });

  return sessionSyncPromise;
}

export const refreshToken = async () =>
  coordinateAuthTokenRefresh(async () => {
    try {
      const previousRefreshMarker = getAuthRefreshMarker();
      const res = await fetchWithIdempotency('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();
      if (res.status === 409) {
        const hasCompletedRefresh = await waitForAuthRefreshMarkerChange(previousRefreshMarker);
        if (!hasCompletedRefresh) return { success: false, isProcessing: true };

        const hasSynchronized = await syncAuthSessionFromCookies();
        const synchronizedAccessToken = useAuthStore.getState().accessToken;
        if (!hasSynchronized) return { success: false, isProcessing: true };
        if (!synchronizedAccessToken) return { success: false };

        return {
          success: true,
          data: { access_token: synchronizedAccessToken },
        };
      }
      if (!res.ok || !data.access_token) return { success: false };

      return {
        success: true,
        data: { access_token: data.access_token },
      };
    } catch {
      return { success: false };
    }
  }, syncAuthStateFromAccessToken);

export const forgotPassword = async (email: string) => {
  const response = await fetchAPI<{ message: string }>(
    `${process.env.NEXT_PUBLIC_API_URL}/forgot-password`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }
  );

  return {
    success: response.success,
    message: response.message,
  };
};

export const resendActivationEmail = async (email: string): Promise<ApiResponse<null>> => {
  const response = await fetchAPI<null>(
    `${process.env.NEXT_PUBLIC_API_URL}/resend-activation-email`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    }
  );

  return {
    ...response,
    message: response.message || 'Activation link sent successfully',
  };
};

export const resetPassword = async (
  email: string,
  token: string,
  password: string,
  password_confirmation: string
) => {
  const response = await fetchAPI<{ message: string }>(
    `${process.env.NEXT_PUBLIC_API_URL}/reset-password`,
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        token,
        password,
        password_confirmation,
      }),
      skipAuth: true,
    }
  );

  return {
    success: response.success,
    message: response.message,
  };
};

export const clear2FA = async () => {
  try {
    const res = await fetchWithIdempotency('/api/auth/2fa/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error('Failed to clear 2FA state');
    }

    return { success: true };
  } catch (error) {
    console.error('Error clearing 2FA:', error);
    throw error;
  }
};

export const verify2FA = async (code: string) => {
  const res = await fetchWithIdempotency('/api/auth/2fa/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: '2FA verification failed' }));
    throw new Error(errorData.error || '2FA verification failed');
  }

  const data = await res.json();

  if (!data.access_token) {
    throw new Error('Invalid response from server');
  }

  const decodedToken = jwtDecode<JwtPayload>(data.access_token);

  return {
    data: decodedToken,
    fe_app_url: data.fe_app_url,
  };
};

export const clearAuthCookies = async () => {
  const didClear = await fetchIdempotentCleanup('/api/auth/clear-auth-cookies', {
    method: 'POST',
  });

  if (!didClear) {
    console.warn('Failed to clear auth cookies');
    return { success: false };
  }

  return { success: true };
};
